import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import { BRAND, NAV } from "@/lib/site";
import { Instagram, Facebook, Linkedin, Twitter, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer({ onDonate }) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const linkFor = (item) => {
    if (item.type === "route") return item.href;
    return isHome ? item.href : "/" + item.href;
  };
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    setPending(true);
    try {
      const r = await axios.post(`${API}/newsletter`, { email });
      if (r.data?.already_subscribed) {
        toast.success("You're already on the list — thank you!");
      } else {
        toast.success("Welcome to the circle. Asante!");
      }
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe — please try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-navy text-white pt-20 pb-10"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${BRAND.logoIcon}`}
                alt="Nguvu Ya Mwanamke Foundation"
                className="h-14 w-auto object-contain"
                onError={(e) => { e.currentTarget.src = BRAND.logo; }}
              />
              <div className="h-10 w-px bg-brand-gold" />
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.28em] text-brand-gold font-semibold">
                  Established · Dar es Salaam
                </div>
                <div className="font-serif italic text-xl text-white tracking-tight mt-1">
                  Nguvu Ya Mwanamke
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/75 max-w-md leading-relaxed">
              {BRAND.tagline}
            </p>

            <form
              onSubmit={subscribe}
              data-testid="newsletter-form"
              className="mt-7 flex items-center gap-2 max-w-md"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                placeholder="Your email for updates"
                className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3 placeholder:text-white/50 outline-none focus:border-brand-gold/70 focus:ring-2 focus:ring-brand-gold/30"
                required
              />
              <button
                type="submit"
                disabled={pending}
                data-testid="newsletter-submit-button"
                className="btn-gold disabled:opacity-60"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold">
              Explore
            </div>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  {n.type === "route" ? (
                    <Link
                      to={n.href}
                      data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-white/80 hover:text-white underline-link"
                    >
                      {n.label}
                    </Link>
                  ) : (
                    <a
                      href={linkFor(n)}
                      data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-white/80 hover:text-white underline-link"
                    >
                      {n.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-gold">
              Contact
            </div>
            <ul className="mt-5 space-y-3 text-white/80 text-sm">
              <li>{BRAND.email}</li>
              <li>{BRAND.phone}</li>
              <li>{BRAND.address}</li>
            </ul>
            <div className="mt-7 flex items-center gap-3">
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-navy text-white flex items-center justify-center transition-all"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={BRAND.socials.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-navy text-white flex items-center justify-center transition-all"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={BRAND.socials.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                data-testid="social-linkedin"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-navy text-white flex items-center justify-center transition-all"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href={BRAND.socials.twitter}
                target="_blank"
                rel="noreferrer"
                aria-label="Twitter / X"
                data-testid="social-twitter"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-navy text-white flex items-center justify-center transition-all"
              >
                <Twitter className="h-4 w-4" />
              </a>
            </div>
            <button
              onClick={onDonate}
              data-testid="footer-donate-button"
              className="btn-gold mt-7"
            >
              Donate Now
            </button>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/55">
          <div>
            © {new Date().getFullYear()} Nguvu Ya Mwanamke Foundation. All
            rights reserved.
          </div>
          <div className="italic font-serif text-white/70">
            Empowering Women &amp; Children. Changing Lives.
          </div>
        </div>
      </div>
    </footer>
  );
}
